import { Box, Collapse, Divider } from '@mui/material'

import { ReactComponent as CalendarMainLogo } from 'assets/calendar_logo.svg'
import { ReactComponent as HoofLogo } from 'assets/hoof-icon.svg'
import FilterContent from 'components/cattles/lamenessPage/shared/filterElements/FilterContent'
import Input from 'components/shared/textFields/Input'
import useLamenessFilter from 'hook/query/useLamenessFilter'
import { useDebounce } from 'hook/useDebounce'
import { useFilterToggle } from 'hook/useFilterToggle'
import useCowsNextPage from 'hook/useNextPage'
import { ICowsList } from 'models/ICattlesData'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CattlesApiService from 'services/cattlesApiServices'
import { COLORS } from 'styles/colors'
import TabsFilter from 'ui/breadCrumbs/TabsFilter'
import CustomButton from 'ui/buttons/CustomButton'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import SelectedInfoBlock from 'ui/SelectedInfoBlock'
import TooltipHover from 'ui/TooltipHover'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { isTablet } from 'variables'
import { useCheckboxSelection } from '../hook/useCheckboxSelection'
import { TrimStatus } from '../models'
import { titleForTrimFilter } from '../shared'
import TrimTable from '../shared/TrimTable'

const TrimList = () => {
	const navigate = useNavigate()

	const [page, setPage] = useState<number>(1)
	const initialPageSize = 36
	const {
		activeFilters,
		openFilter,
		toggleFilter,
		setActiveFilters,
		setOpenFilter,
		handleDayChange,
		returnInitialProps,
		turnOnFilters,
		isFetchComplete,
		isEqualFilter,
		isEqualLamenessFilter,
		isEqualGroupsFilter,
		isEqualTrimFilter,
		isEqualRespPersonsFilter,
	} = useLamenessFilter()

	const [debouncedValue, handleChange, value] = useDebounce()
	const propForTable = {
		params: activeFilters,
		isFetchComplete,
		page,
		setPage,
		initialPageSize,
		isFetchCowsListEnabled: !openFilter,
		initialTabs: initialTabs,
		tag_id_search: debouncedValue,
	}

	const navigateOnEditTrimPage = () => {
		!isDisableCowEditButton &&
			navigate('/cattles/trim/edit', { state: selectedCows })
	}
	const navigateOnCheckupTrimPage = () => {
		!isDisableCowEditButton &&
			navigate('/cattles/trim/check-up', { state: selectedCows })
	}

	const {
		cowsList,
		totalCountPages,
		fetchingCowsList,
		availableTabs,
		availableTags,
	} = useCowsNextPage(propForTable)

	const fetchAllCows = useCallback(async () => {
		const pageSize = 100
		let currentPage = 1
		const allCows: ICowsList[] = []
		let total = totalCountPages
		let hasMorePages = true

		while (hasMorePages) {
			const response = await CattlesApiService.getCowsList(
				null,
				undefined,
				currentPage,
				pageSize,
				activeFilters,
				debouncedValue,
			)
			const results = response.data.results ?? []
			allCows.push(...results)
			total = response.data.count ?? total
			hasMorePages = results.length > 0 && allCows.length < total
			currentPage += 1
		}

		return allCows
	}, [activeFilters, debouncedValue, totalCountPages])

	const {
		selectedCows,
		showOnlySelected,
		setShowOnlySelected,
		toggleCheckbox,
		toggleAllCheckboxes,
		resetCheckboxes,
		isAllSelected,
		isSelectingAll,
	} = useCheckboxSelection({
		page,
		cowsList,
		totalCount: totalCountPages,
		fetchAllCows,
	})

	useEffect(() => {
		resetCheckboxes()
	}, [activeFilters, debouncedValue, resetCheckboxes])

	const isDisableCowEditButton = selectedCows?.length === 0

	const visibleCowsList = useMemo(() => {
		return showOnlySelected ? selectedCows : cowsList
	}, [cowsList, selectedCows, showOnlySelected])

	const handleTagsToggle = useFilterToggle(
		'tags',
		availableTags,
		setActiveFilters,
	)

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
			<CardStandart sx={{ padding: '24px' }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<TypographyHeader>Список голов</TypographyHeader>
					<Box
						display={'flex'}
						flexDirection={'row'}
						alignItems={'center'}
						gap={'16px'}
					>
						<Input
							width={'400px'}
							value={value}
							onChange={handleChange}
							label={'Поиск по номеру бирки'}
							size='small'
						/>
						{/*
						<AutocompleteInput
							label={'Поиск по номеру бирки'}
							widthForInput='340px'
							isCheckbox
							startIcon={<SearchIcon stroke={COLORS.icon} />}
							availableList={availableTags}
							onClick={handleTagsToggle}
							selectedValues={activeFilters?.tags}
							isShowTags={false}
						/> */}
						<TooltipHover
							title={
								'Выберите одну или несколько голов для выполнения действия'
							}
							placement={'top'}
							maxWidth={230}
							isShowTooltip={isDisableCowEditButton}
						>
							<CustomButton
								title={isTablet ? null : 'Планировать'}
								onClick={navigateOnEditTrimPage}
								iconElement={
									<CalendarMainLogo
										stroke={
											isDisableCowEditButton ? COLORS.inputBorder : COLORS.main
										}
									/>
								}
								disable={isDisableCowEditButton}
								sx={{ height: '40px' }}
							/>
						</TooltipHover>
						<TooltipHover
							title={
								'Выберите одну или несколько голов для выполнения действия'
							}
							placement={'top'}
							maxWidth={230}
							isShowTooltip={isDisableCowEditButton}
						>
							<CustomButton
								title={isTablet ? null : 'Провести обрезку'}
								onClick={navigateOnCheckupTrimPage}
								iconElement={
									<HoofLogo
										stroke={
											isDisableCowEditButton ? COLORS.inputBorder : COLORS.main
										}
									/>
								}
								disable={isDisableCowEditButton}
								sx={{ height: '40px' }}
							/>
						</TooltipHover>
					</Box>
				</Box>
				<Box
					display={'flex'}
					flexDirection={'row'}
					alignItems={'flex-end'}
					gap={'24px'}
				>
					<TabsFilter
						availableTabs={availableTabs}
						onClick={useCallback(
							key => {
								returnInitialProps('all')
								if (key === TrimStatus.Missed) {
									setActiveFilters(prev => {
										return {
											...prev,
											isLateTrims: true,
										}
									})
								}
							},
							[returnInitialProps, setActiveFilters],
						)}
					/>
					<FilterContent
						activeFilters={activeFilters}
						openFilter={openFilter}
						toggleFilter={toggleFilter}
						setActiveFilters={setActiveFilters}
						setOpenFilter={setOpenFilter}
						handleDayChange={handleDayChange}
						returnInitialProps={returnInitialProps}
						turnOnFilters={turnOnFilters}
						titleForFilter={titleForTrimFilter}
						isEqualFilter={isEqualFilter}
						isEqualLamenessFilter={isEqualLamenessFilter}
						isEqualTrimFilter={isEqualTrimFilter}
						isEqualGroupsFilter={isEqualGroupsFilter}
						isEqualRespPersonsFilter={isEqualRespPersonsFilter}
					/>
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
					}}
				>
					<TrimTable
						selectedCheckboxes={selectedCows.map(cow => cow.tag_id)}
						cattlesInfo={visibleCowsList}
						fetchingCowsList={fetchingCowsList}
						toggleCheckbox={toggleCheckbox}
						toggleAllCheckboxes={toggleAllCheckboxes}
						isAllSelected={isAllSelected}
						isSelectingAll={isSelectingAll}
					/>

					<Divider />
					<Collapse in={selectedCows.length > 0}>
						<SelectedInfoBlock
							selectedCheckboxes={selectedCows.map(cow => cow.tag_id)}
							resetCheckboxes={resetCheckboxes}
							setShowOnlySelected={setShowOnlySelected}
							showOnlySelected={showOnlySelected}
						/>
					</Collapse>
					<CustomPagination
						onChangeFromServer={setPage}
						count={initialPageSize}
						totalCount={
							showOnlySelected ? selectedCows?.length : totalCountPages
						}
						isResetPage={!isEqualFilter}
					/>
				</Box>
			</CardStandart>
		</Box>
	)
}

export default TrimList

const initialTabs = [
	{
		key: 'all',
		title: 'Все',
		description: null,
	},
	{
		key: TrimStatus.Missed,
		title: 'Пропущено',
		description: null,
	},
]
